import { useEffect, useState } from 'react'
import { EXPORT_GUIDES, type ExportGuide, type GuideScreenshot } from './exportGuides'

const STATUS_BADGES: Record<ExportGuide['status'], { label: string; className: string }> = {
  ready: { label: 'Bereit', className: 'border-gain/40 text-gain' },
  planned: { label: 'Import folgt', className: 'border-hairline text-muted' },
}

interface ExportGuideSectionProps {
  selectedBrokerType: string | null
}

export function ExportGuideSection({ selectedBrokerType }: ExportGuideSectionProps) {
  const [openBroker, setOpenBroker] = useState<string | null>(selectedBrokerType ?? 'ibkr')

  // Account-Wahl gewinnt gegen manuelles Toggle (Spec: Smart-Expand)
  useEffect(() => {
    if (selectedBrokerType) setOpenBroker(selectedBrokerType)
  }, [selectedBrokerType])

  return (
    <section className="hairline mt-6 flex flex-col gap-3 border bg-panel p-4 md:p-6">
      <h2 className="tracked-caps text-xs text-muted">So kommst du an die Export-Dateien</h2>
      {EXPORT_GUIDES.map((guide) => (
        <GuidePanel
          key={guide.brokerType}
          guide={guide}
          open={openBroker === guide.brokerType}
          onToggle={() =>
            setOpenBroker((current) => (current === guide.brokerType ? null : guide.brokerType))
          }
        />
      ))}
    </section>
  )
}

interface GuidePanelProps {
  guide: ExportGuide
  open: boolean
  onToggle: () => void
}

function GuidePanel({ guide, open, onToggle }: GuidePanelProps) {
  const badge = STATUS_BADGES[guide.status]

  return (
    <div className="hairline border bg-void">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 p-3 text-left text-sm text-ink hover:bg-panel"
      >
        <span>{guide.title}</span>
        <span className={`shrink-0 border px-2 py-0.5 text-xs ${badge.className}`}>
          {badge.label}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-4 border-t border-hairline p-3 md:p-4">
          <p className="text-sm text-muted">{guide.intro}</p>

          <dl className="grid gap-1 text-xs text-muted md:grid-cols-2">
            {guide.facts.map((fact) => (
              <div key={fact.label} className="flex gap-2">
                <dt className="tracked-caps shrink-0">{fact.label}:</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>

          <ol className="flex flex-col gap-3">
            {guide.steps.map((step, idx) => (
              <li key={step.title} className="text-sm">
                <p className="text-ink">
                  {idx + 1}. {step.title}
                </p>
                <p className="mt-1 text-muted">{step.text}</p>
                {step.screenshot && <ScreenshotPlaceholder screenshot={step.screenshot} />}
              </li>
            ))}
          </ol>

          {guide.outro && <p className="text-sm text-bernstein">{guide.outro}</p>}
        </div>
      )}
    </div>
  )
}

function ScreenshotPlaceholder({ screenshot }: { screenshot: GuideScreenshot }) {
  return (
    <figure className="mt-2 border border-dashed border-hairline bg-panel p-3 text-xs text-muted">
      <span className="tracked-caps block">Screenshot folgt</span>
      <span className="block">{screenshot.file}</span>
      <figcaption className="mt-1 italic">{screenshot.caption}</figcaption>
    </figure>
  )
}
